import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  User, Building2, MapPin, Clock, BadgeCheck, Camera, Save,
  IndianRupee, Loader2, Plus, X, Hash, CalendarCheck,
  Upload, FileCheck, AlertCircle, ShieldCheck, File
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const SERVICE_OPTIONS = [
  "GST Filing", "Income Tax Filing", "Audit", "Company Registration",
  "Business Consulting", "Transfer Pricing", "NRI Taxation",
  "Bookkeeping", "Payroll Management", "TDS Returns",
  "ROC Compliance", "FEMA Advisory", "Startup Advisory",
];

const SPECIALIZATION_OPTIONS = [
  "GST Expert", "Income Tax", "Audit", "Company Registration",
  "Startup Advisory", "Transfer Pricing", "NRI Taxation",
];

const CITIES = [
  "Mumbai", "Delhi", "Chennai", "Ahmedabad", "Jaipur",
  "Hyderabad", "Kolkata", "Pune", "Bangalore", "Lucknow",
  "Chandigarh", "Kochi", "Indore", "Bhopal",
];

const AVAILABILITY_OPTIONS = [
  { value: "available", label: "Available", color: "bg-green-500" },
  { value: "busy", label: "Busy", color: "bg-yellow-500" },
  { value: "unavailable", label: "Unavailable", color: "bg-red-500" },
];

interface ProfileForm {
  name: string;
  firm_name: string;
  membership_number: string;
  city: string;
  state: string;
  experience_years: number;
  bio: string;
  email: string;
  phone: string;
  website: string;
  pricing_hourly: number | null;
  pricing_monthly: number | null;
  services: string[];
  specializations: string[];
  availability: string;
  avatar_url: string | null;
}

const DOCUMENT_TYPES = [
  { key: "membership_certificate", label: "CA Membership Certificate", desc: "ICAI membership certificate" },
  { key: "pan_card", label: "PAN Card", desc: "Permanent Account Number card" },
  { key: "firm_registration", label: "Firm Registration", desc: "Firm registration certificate" },
];

interface VerDoc {
  id: string;
  document_type: string;
  file_name: string;
  file_url: string;
  status: string;
  reviewer_notes: string | null;
  created_at: string;
}

function VerificationDocumentsSection({ profileId, userId }: { profileId: string; userId: string }) {
  const { toast } = useToast();
  const [docs, setDocs] = useState<VerDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    fetchDocs();
  }, [profileId]);

  const fetchDocs = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("ca_verification_documents")
      .select("*")
      .eq("ca_profile_id", profileId) as any;
    if (data) setDocs(data);
    setLoading(false);
  };

  const uploadDoc = async (docType: string, file: globalThis.File) => {
    setUploading(docType);
    const ext = file.name.split(".").pop();
    const path = `${userId}/${docType}.${ext}`;

    const { error: uploadErr } = await supabase.storage
      .from("ca-verification-docs")
      .upload(path, file, { upsert: true });

    if (uploadErr) {
      toast({ title: "Upload failed", description: uploadErr.message, variant: "destructive" });
      setUploading(null);
      return;
    }

    // Get signed URL (bucket is private)
    const { data: urlData } = await supabase.storage.from("ca-verification-docs").createSignedUrl(path, 60 * 60 * 24 * 365);
    const fileUrl = urlData?.signedUrl || "";

    const existing = docs.find((d) => d.document_type === docType);
    if (existing) {
      await supabase.from("ca_verification_documents").update({
        file_name: file.name,
        file_url: fileUrl,
        file_size: file.size,
        status: "pending",
        reviewer_notes: null,
        updated_at: new Date().toISOString(),
      } as any).eq("id", existing.id);
    } else {
      await supabase.from("ca_verification_documents").insert({
        ca_profile_id: profileId,
        user_id: userId,
        document_type: docType,
        file_name: file.name,
        file_url: fileUrl,
        file_size: file.size,
      } as any);
    }

    toast({ title: "Document uploaded", description: "Submitted for verification." });
    fetchDocs();
    setUploading(null);
  };

  const getDocStatus = (docType: string) => {
    const doc = docs.find((d) => d.document_type === docType);
    if (!doc) return null;
    return doc;
  };

  const statusBadge = (status: string) => {
    const map: Record<string, { label: string; cls: string; icon: any }> = {
      pending: { label: "Under Review", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20", icon: Clock },
      approved: { label: "Approved", cls: "bg-green-500/10 text-green-600 border-green-500/20", icon: FileCheck },
      rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive border-destructive/20", icon: AlertCircle },
    };
    const info = map[status] || map.pending;
    return (
      <Badge variant="outline" className={`text-[10px] gap-1 ${info.cls}`}>
        <info.icon className="h-3 w-3" /> {info.label}
      </Badge>
    );
  };

  const allApproved = DOCUMENT_TYPES.every((dt) => {
    const doc = docs.find((d) => d.document_type === dt.key);
    return doc?.status === "approved";
  });

  return (
    <Card className="bg-card border-border">
      <CardHeader className="p-4 pb-2">
        <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-accent" /> Verification Documents
          {allApproved && (
            <Badge className="bg-green-500/10 text-green-600 border-green-500/20 text-[10px] gap-1 ml-2">
              <BadgeCheck className="h-3 w-3" /> Verified
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 pt-0 space-y-3">
        <p className="text-xs text-muted-foreground">
          Upload your verification documents to get the "Verified CA" badge on your profile. All documents are reviewed by our team.
        </p>

        {loading ? (
          <div className="py-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-3">
            {DOCUMENT_TYPES.map((dt) => {
              const doc = getDocStatus(dt.key);
              const isUploading = uploading === dt.key;

              return (
                <div key={dt.key} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border/50">
                  <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <File className="h-4 w-4 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground">{dt.label}</p>
                    <p className="text-[10px] text-muted-foreground">{dt.desc}</p>
                    {doc && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground truncate">{doc.file_name}</span>
                        {statusBadge(doc.status)}
                      </div>
                    )}
                    {doc?.reviewer_notes && doc.status === "rejected" && (
                      <p className="text-[10px] text-destructive mt-1">Reason: {doc.reviewer_notes}</p>
                    )}
                  </div>
                  <div>
                    <input
                      ref={(el) => { inputRefs.current[dt.key] = el; }}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadDoc(dt.key, f);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      size="sm"
                      variant={doc ? "outline" : "default"}
                      className="text-xs gap-1 h-7"
                      disabled={isUploading || doc?.status === "approved"}
                      onClick={() => inputRefs.current[dt.key]?.click()}
                    >
                      {isUploading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Upload className="h-3 w-3" />
                      )}
                      {doc?.status === "approved" ? "Verified" : doc ? "Re-upload" : "Upload"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const CAProfileEditPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [newService, setNewService] = useState("");

  const [form, setForm] = useState<ProfileForm>({
    name: "", firm_name: "", membership_number: "", city: "", state: "",
    experience_years: 0, bio: "", email: "", phone: "", website: "",
    pricing_hourly: null, pricing_monthly: null,
    services: [], specializations: [], availability: "available", avatar_url: null,
  });

  useEffect(() => {
    if (user) loadProfile();
  }, [user]);

  const loadProfile = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("ca_profiles")
      .select("*")
      .eq("user_id", user!.id)
      .maybeSingle();

    if (data) {
      setProfileId(data.id);
      setForm({
        name: data.name || "",
        firm_name: data.firm_name || "",
        membership_number: (data as any).membership_number || "",
        city: data.city || "",
        state: data.state || "",
        experience_years: data.experience_years || 0,
        bio: data.bio || "",
        email: data.email || "",
        phone: data.phone || "",
        website: data.website || "",
        pricing_hourly: data.pricing_hourly,
        pricing_monthly: data.pricing_monthly,
        services: data.services || [],
        specializations: data.specializations || [],
        availability: (data as any).availability || "available",
        avatar_url: data.avatar_url,
      });
    } else {
      setIsNew(true);
      setForm((f) => ({ ...f, email: user?.email || "" }));
    }
    setLoading(false);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);

    const ext = file.name.split(".").pop();
    const path = `${user.id}/avatar.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("ca-avatars")
      .upload(path, file, { upsert: true });

    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
    } else {
      const { data: urlData } = supabase.storage.from("ca-avatars").getPublicUrl(path);
      setForm((f) => ({ ...f, avatar_url: urlData.publicUrl + "?t=" + Date.now() }));
      toast({ title: "Photo uploaded!" });
    }
    setUploading(false);
  };

  const toggleItem = (field: "services" | "specializations", item: string) => {
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(item)
        ? f[field].filter((s) => s !== item)
        : [...f[field], item],
    }));
  };

  const addCustomService = () => {
    if (newService.trim() && !form.services.includes(newService.trim())) {
      setForm((f) => ({ ...f, services: [...f.services, newService.trim()] }));
      setNewService("");
    }
  };

  const saveProfile = async () => {
    if (!user) return;
    if (!form.name.trim() || !form.city) {
      toast({ title: "Name and City are required", variant: "destructive" });
      return;
    }
    setSaving(true);

    const payload = {
      user_id: user.id,
      name: form.name.trim(),
      firm_name: form.firm_name.trim() || null,
      membership_number: form.membership_number.trim() || null,
      city: form.city,
      state: form.state.trim() || null,
      experience_years: form.experience_years,
      bio: form.bio.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      website: form.website.trim() || null,
      pricing_hourly: form.pricing_hourly,
      pricing_monthly: form.pricing_monthly,
      services: form.services,
      specializations: form.specializations,
      availability: form.availability,
      avatar_url: form.avatar_url,
    };

    let error;
    if (profileId) {
      ({ error } = await supabase.from("ca_profiles").update(payload as any).eq("id", profileId));
    } else {
      const { data, error: insErr } = await supabase.from("ca_profiles").insert(payload as any).select().single();
      error = insErr;
      if (data) setProfileId((data as any).id);
    }

    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Profile saved!" });
      setIsNew(false);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <User className="h-6 w-6 text-accent" />
            {isNew ? "Create Your CA Profile" : "Manage Your Profile"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isNew ? "Set up your professional profile to appear in the CA Marketplace" : "Keep your professional profile up to date"}
          </p>
        </div>
        <Button onClick={saveProfile} disabled={saving} className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Profile
        </Button>
      </div>

      {/* Profile Photo & Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border md:col-span-1">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-xs font-semibold">Profile Photo</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex flex-col items-center gap-3">
            <div
              className="w-28 h-28 rounded-2xl bg-primary/10 flex items-center justify-center overflow-hidden cursor-pointer relative group"
              onClick={() => fileInputRef.current?.click()}
            >
              {form.avatar_url ? (
                <img src={form.avatar_url} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <span className="text-primary font-bold text-4xl">{form.name.charAt(0) || "?"}</span>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {uploading ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : (
                  <Camera className="h-6 w-6 text-white" />
                )}
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
            <p className="text-[10px] text-muted-foreground text-center">Click to upload photo</p>

            {/* Availability */}
            <div className="w-full space-y-2 pt-2 border-t border-border/30">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <CalendarCheck className="h-3.5 w-3.5" /> Availability
              </Label>
              <Select value={form.availability} onValueChange={(v) => setForm((f) => ({ ...f, availability: v }))}>
                <SelectTrigger className="text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABILITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      <span className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${o.color}`} />
                        {o.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border md:col-span-2">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-xs font-semibold">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Full Name *</Label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="CA Full Name" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Firm Name</Label>
                <Input value={form.firm_name} onChange={(e) => setForm((f) => ({ ...f, firm_name: e.target.value }))} placeholder="Your firm name" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Hash className="h-3 w-3" /> Membership Number</Label>
                <Input value={form.membership_number} onChange={(e) => setForm((f) => ({ ...f, membership_number: e.target.value }))} placeholder="e.g., FCA-123456" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><Clock className="h-3 w-3" /> Years of Experience</Label>
                <Input type="number" min={0} value={form.experience_years} onChange={(e) => setForm((f) => ({ ...f, experience_years: parseInt(e.target.value) || 0 }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> City *</Label>
                <Select value={form.city} onValueChange={(v) => setForm((f) => ({ ...f, city: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select city" /></SelectTrigger>
                  <SelectContent>
                    {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input value={form.state} onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))} placeholder="State" className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Bio / About</Label>
              <Textarea
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                placeholder="Describe your expertise, approach, and what makes you unique..."
                rows={3}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contact & Pricing */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-card border-border">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-xs font-semibold">Contact Details</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="ca@example.com" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+91 98765 43210" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Website</Label>
              <Input value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="https://yourfirm.com" className="mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
              <IndianRupee className="h-3.5 w-3.5" /> Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div>
              <Label className="text-xs">Hourly Rate (₹)</Label>
              <Input
                type="number" min={0}
                value={form.pricing_hourly ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, pricing_hourly: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder="e.g., 2000"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Monthly Retainer (₹)</Label>
              <Input
                type="number" min={0}
                value={form.pricing_monthly ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, pricing_monthly: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder="e.g., 25000"
                className="mt-1"
              />
            </div>
            <p className="text-[10px] text-muted-foreground">Leave blank if you prefer to quote per project</p>
          </CardContent>
        </Card>
      </div>

      {/* Specializations */}
      <Card className="bg-card border-border">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-xs font-semibold">Specializations</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0">
          <div className="flex flex-wrap gap-2">
            {SPECIALIZATION_OPTIONS.map((s) => (
              <Badge
                key={s}
                variant={form.specializations.includes(s) ? "default" : "outline"}
                className="cursor-pointer text-xs transition-all"
                onClick={() => toggleItem("specializations", s)}
              >
                {form.specializations.includes(s) && <BadgeCheck className="h-3 w-3 mr-1" />}
                {s}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Services */}
      <Card className="bg-card border-border">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="text-xs font-semibold">Services Offered</CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          <div className="flex flex-wrap gap-2">
            {SERVICE_OPTIONS.map((s) => (
              <Badge
                key={s}
                variant={form.services.includes(s) ? "default" : "outline"}
                className="cursor-pointer text-xs transition-all"
                onClick={() => toggleItem("services", s)}
              >
                {form.services.includes(s) && <BadgeCheck className="h-3 w-3 mr-1" />}
                {s}
              </Badge>
            ))}
          </div>

          {/* Custom services added by user */}
          {form.services.filter((s) => !SERVICE_OPTIONS.includes(s)).length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-border/30">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider self-center">Custom:</span>
              {form.services.filter((s) => !SERVICE_OPTIONS.includes(s)).map((s) => (
                <Badge key={s} variant="secondary" className="text-xs gap-1">
                  {s}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => toggleItem("services", s)} />
                </Badge>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={newService}
              onChange={(e) => setNewService(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomService(); } }}
              placeholder="Add custom service..."
              className="text-xs h-8 flex-1"
            />
            <Button size="sm" variant="outline" className="h-8 px-3" onClick={addCustomService} disabled={!newService.trim()}>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Verification Documents */}
      {profileId && (
        <VerificationDocumentsSection profileId={profileId} userId={user!.id} />
      )}

      {/* Save */}
      <div className="flex justify-end pb-4">
        <Button onClick={saveProfile} disabled={saving} size="lg" className="gap-1.5">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isNew ? "Create Profile" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default CAProfileEditPage;
